/**
 * Shell for the V3 SPA.
 *
 * Hosts the one flow slice 1 delivers as the same staged journey the V2 portal uses: a
 * landing screen, then country / region selection, then the domestic request wizard.
 * V3 has no router, so the stage is a piece of local state rather than a route. Replaced
 * by the forked V2 client (full Fluent UI, Redux Toolkit, ARS login) as porting
 * proceeds.
 *
 * The shell is gated twice: the portal carries law-enforcement data, so an anonymous
 * visitor sees only the sign-in screen, and a signed-in visitor sees only the
 * government-identity screens until an official agency address is confirmed. Neither gate
 * lets the stages below it mount.
 *
 * After sign-in the shell asks the API whether this agent must verify their agency address
 * again. An agent whose last passcode is still current goes straight in, and anyone else
 * gets the government-identity screens. If the answer cannot be read, the shell says so and
 * offers a retry (or a fresh sign-in when the token was refused); it never lets the portal
 * mount on a failure.
 *
 * Both gates stand down under `npm run dev`, for the same reason: a developer working on
 * the request wizard or the case list should not have to sign in, nor type an address and
 * a code the local stand-in never mails, to reach them. `npm run dev:npe` serves the same
 * app with both gates live, which is how they are exercised locally.
 */
import { useEffect, useRef, useState } from 'react';
import { makeStyles, shorthands, Spinner, Title3, tokens } from '@fluentui/react-components';
import { RequestWizard } from './features/request-creation/components/RequestWizard/RequestWizard';
import { CaseListView } from './features/case-list/components/CaseListView/CaseListView';
import { LandingPage } from './features/domestic/LandingPage';
import { CountryStep } from './features/domestic/CountryStep';
import { EEvidenceSubmission } from './features/e-evidence/components/EEvidenceSubmission';
import { ProfilePage } from './features/profile/ProfilePage';
import {
  type AgentGateCheck,
  AuthButton,
  checkAgentGate,
  GateErrorPage,
  GovIdVerification,
  SignInPage,
  useAuth,
} from './features/auth';

/**
 * Whether to treat the agency address as already confirmed instead of asking for it. True
 * only while the vite dev server is serving in `development` mode — `npm run dev`.
 *
 * The sibling of `__AUTH_BYPASS__` in main.tsx, substituted by vite's `define` (see
 * vite.config.ts) from its own `command` for the same reason: `import.meta.env.DEV`
 * follows `NODE_ENV`, which vite honours when preset, so it could fold to true in a
 * deployable bundle. In any build this is the literal `false`, so the gate below is a
 * live gate everywhere the portal is actually served.
 */
declare const __VERIFICATION_BYPASS__: boolean;

type PortalStage = 'landing' | 'country' | 'wizard' | 'e-evidence' | 'cases';

/** Where the government-identity gate stands for the signed-in user. */
type GovIdGate = { status: 'checking' } | AgentGateCheck;

const initialGovIdGate: GovIdGate = { status: __VERIFICATION_BYPASS__ ? 'verified' : 'checking' };

const useStyles = makeStyles({
  root: {
    height: '100vh',
    overflow: 'hidden',
    '@supports (height: 100dvh)': {
      height: '100dvh',
    },
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: tokens.colorNeutralBackground2,
  },
  header: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    columnGap: tokens.spacingHorizontalL,
    ...shorthands.padding(tokens.spacingVerticalL, tokens.spacingHorizontalXXL),
    ...shorthands.borderBottom(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke2),
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorBrandForeground1,
  },
  main: {
    flexGrow: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
  },
  stage: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    '&[hidden]': { display: 'none' },
  },
});

export function App() {
  const styles = useStyles();
  const { isAuthenticated, logout } = useAuth();

  const [stage, setStage] = useState<PortalStage>('landing');
  const [showProfile, setShowProfile] = useState(false);
  const accountButton = useRef<HTMLButtonElement>(null);
  const profileWasOpen = useRef(false);
  const [country, setCountry] = useState<string>('');
  const [govIdGate, setGovIdGate] = useState<GovIdGate>(initialGovIdGate);

  useEffect(() => {
    if (profileWasOpen.current && !showProfile) {
      accountButton.current?.focus();
    }
    profileWasOpen.current = showProfile;
  }, [showProfile]);

  // Each sign-in starts unverified and asks the API afresh; signing out drops whatever the
  // previous session established.
  useEffect(() => {
    if (!isAuthenticated) {
      setGovIdGate(initialGovIdGate);
      return;
    }

    if (govIdGate.status !== 'checking') {
      return;
    }

    let cancelled = false;
    void checkAgentGate().then((check) => {
      if (!cancelled) {
        setGovIdGate(check);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, govIdGate]);

  if (!isAuthenticated) {
    return <SignInPage />;
  }

  if (govIdGate.status === 'checking') {
    return <Spinner size="large" label="Checking your account…" />;
  }

  // An answer that could not be read is reported as such, not taken as "verify again":
  // nothing below mounts, but a verified user is not sent through the passcode flow
  // because ARS was briefly unreachable.
  if (govIdGate.status === 'unavailable') {
    return (
      <GateErrorPage
        message={govIdGate.message}
        actionLabel="Try again"
        onAction={() => setGovIdGate({ status: 'checking' })}
      />
    );
  }

  if (govIdGate.status === 'unauthenticated') {
    return (
      <GateErrorPage
        message={govIdGate.message}
        actionLabel="Sign in again"
        onAction={() => void logout()}
      />
    );
  }

  // Signing in with Microsoft identifies an account, not an official: the portal also
  // needs an agency address it can reach the person at. Nothing below this line mounts
  // until that is confirmed.
  if (govIdGate.status === 'required') {
    return <GovIdVerification onVerified={() => setGovIdGate({ status: 'verified' })} />;
  }

  if (govIdGate.status === 'registration') {
    return (
      <GovIdVerification
        registration={govIdGate}
        onVerified={() => setGovIdGate({ status: 'verified' })}
      />
    );
  }

  return (
    <div className={styles.root}>
      <header className={styles.header}>
        <Title3>Microsoft Law Enforcement Portal</Title3>
        <AuthButton accountButtonRef={accountButton} onViewProfile={() => setShowProfile(true)} />
      </header>
      <main className={styles.main}>
        {showProfile && (
          <ProfilePage onBack={() => setShowProfile(false)} />
        )}
        <div className={styles.stage} hidden={showProfile}>
          {stage === 'landing' && (
            <LandingPage
              onStart={() => setStage('country')}
              onViewCases={() => setStage('cases')}
            />
          )}
          {stage === 'country' && (
            <CountryStep
              onSelect={(selected) => {
                setCountry(selected);
                setStage('wizard');
              }}
              onSelectEEvidence={() => setStage('e-evidence')}
              onCancel={() => setStage('landing')}
            />
          )}
          {stage === 'wizard' && (
            <RequestWizard countryName={country} onExit={() => setStage('landing')} />
          )}
          {stage === 'e-evidence' && (
            <EEvidenceSubmission onExit={() => setStage('country')} />
          )}
          {stage === 'cases' && <CaseListView onExit={() => setStage('landing')} />}
        </div>
      </main>
    </div>
  );
}
